import { isAnonymousRoute } from "../auth/anonymousRoutes";
import { ApiError, ApiErrorBody } from "./types";

interface Options extends Omit<RequestInit, "body"> {
  json?: unknown;
  body?: BodyInit;
}

async function request<T>(path: string, opts: Options = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  let body = opts.body;

  if (opts.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(opts.json);
  }
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }

  const res = await fetch(path, {
    ...opts,
    headers,
    body,
    credentials: "same-origin",
  });

  if (res.status === 401) {
    // SPA-wide redirect on session loss for protected pages. Skip it on the
    // public auth routes: those legitimately 401 on the initial loadTenant()
    // probe (no session yet), and a hard redirect there would bounce a freshly
    // opened reset/confirm link to /login before its form can render. On those
    // pages AuthProvider owns the anonymous flow.
    if (
      typeof window !== "undefined" &&
      !isAnonymousRoute(window.location.pathname)
    ) {
      window.location.href = "/login";
    }
    throw new ApiError(401, { error_code: "unauthorized" });
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  let parsed: unknown;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = undefined;
    }
  }

  if (!res.ok) {
    throw new ApiError(
      res.status,
      (parsed as ApiErrorBody) ?? { error: text || "request_failed" },
    );
  }

  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),
  post: <T>(
    path: string,
    json?: unknown,
    options: Omit<Options, "json" | "body" | "method"> = {},
  ) =>
    request<T>(path, { ...options, method: "POST", json }),
  put: <T>(path: string, json?: unknown) =>
    request<T>(path, { method: "PUT", json }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
